import React, { useMemo, useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import type { CpGpMatchedProduct } from '../../../../../../models/Coupang';
import type { RestructuredProduct } from '../../../../../../infrastructure/SourcingClient';
import { sourcingClient } from '../../../../../../infrastructure/SourcingClient';
import { verifiedImagesClient } from '../../../../../../infrastructure/VerifiedImagesClient';
import { convertToCloudFrontUrl, convertToS3Url } from '../utils/imageUtils';
import type { ImageEditorTool, ImageEditMode } from '../types';
import { PDPImageEditor, type PDPImageEditorRef } from './PDPImageEditor';
import { THUImageEditor } from './THUImageEditor';
import { SkinSeoulProductType } from '../../../../../../models/ProductTypes';

interface ImageSelection {
  thumbnails: Record<string, string>;
  gallery: string[];
  pdpImages: string[];
}

interface ImageEditorCanvasProps {
  readonly matchedProduct: CpGpMatchedProduct;
  readonly restructuredProducts: RestructuredProduct[];
  readonly editMode: ImageEditMode;
  readonly imageSelection: ImageSelection;
  readonly onImageSelectionChange: (selection: ImageSelection) => void;
  readonly selectedTool: ImageEditorTool;
  readonly onSaveEditedImage: (originalImageUrl: string, editedImageUrl: string) => void;
  readonly textSettings?: { color: string; fontSize: number };
  readonly deletedDetailedImages: Set<string>;
  readonly onDeleteDetailedImage: (url: string) => void;
  readonly onDeletePdpImage?: (url: string) => Promise<void>;
  readonly getSKUFromCpItemId?: (cpItemId: string) => string | undefined;
  readonly onUploadThumbnailImmediately?: (dataUrl: string, productKey: string, cpItemId: string) => Promise<string>;
  readonly originalGalleryRef: React.MutableRefObject<string[]>;
  readonly verifiedImages: string[];
  readonly refetchVerifiedImages: () => Promise<void>;
  readonly pdpEditorRef?: React.RefObject<PDPImageEditorRef>;
}

export const ImageEditorCanvas = ({
  matchedProduct,
  restructuredProducts,
  editMode,
  imageSelection,
  onImageSelectionChange,
  selectedTool,
  onSaveEditedImage,
  textSettings,
  deletedDetailedImages,
  onDeleteDetailedImage,
  onDeletePdpImage,
  getSKUFromCpItemId,
  onUploadThumbnailImmediately,
  originalGalleryRef,
  verifiedImages,
  refetchVerifiedImages,
  pdpEditorRef,
}: ImageEditorCanvasProps) => {
  const [draggedPDPIndex, setDraggedPDPIndex] = useState<number | null>(null);
  const [translatingImages, setTranslatingImages] = useState<Set<string>>(new Set());
  const [imageDimensions, setImageDimensions] = useState<Record<string, { width: number; height: number }>>({});
  const [preloadedImages, setPreloadedImages] = useState<Record<string, HTMLImageElement>>({});
  const [saveLoading, setSaveLoading] = useState(false);
  const fileInputRef = useState<Record<string, HTMLInputElement | null>>({})[0];

  const coupangProduct = matchedProduct.cpDetailedProduct;

  useEffect(() => {
    imageSelection.pdpImages.forEach((url) => {
      if (!preloadedImages[url]) {
        // Convert to S3 URL for Konva editor (CORS compatibility)
        const s3Url = url.startsWith('data:') ? url : convertToS3Url(url);
        const img = new Image();
        if (!url.startsWith('data:')) {
          img.crossOrigin = 'anonymous';
        }
        img.onload = () => {
          setPreloadedImages((prev) => ({ ...prev, [url]: img }));
        };
        img.onerror = () => {
          console.error('Failed to pre-load image:', url.substring(0, 60), '-> S3:', s3Url.substring(0, 60));
        };
        img.src = s3Url;
      }
    });
  }, [imageSelection.pdpImages, preloadedImages]);

  const productsThatNeedThumbnails = useMemo(() => {
    // Variable and Simple products need thumbnails, NOT variations
    const productsNeedingThumbnails = restructuredProducts.filter((p) => p.type === 'variable' || p.type === 'simple');

    return productsNeedingThumbnails.map((product) => {
      const productKey = `${product.cpItemId || ''}_${product.type}_${product.attributePack}_${product.attributeSize}_${product.attributeColor}`;

      // Prefer verified images from imageSelection.thumbnails if available
      let recommendedImageUrl: string | undefined = imageSelection.thumbnails[productKey];

      // If no verified thumbnail, try to use optionImageUrl from restructured products
      if (!recommendedImageUrl) {
        if (product.type === 'variable') {
          const allVariations = restructuredProducts.filter((p) => p.type === 'variation');
          const firstVariation = allVariations[0];
          recommendedImageUrl = firstVariation?.optionImageUrl;
        } else {
          // For simple products, use their own optionImageUrl
          recommendedImageUrl = product.optionImageUrl;
        }

        const heroImages = coupangProduct?.heroImageUrls || [];

        // Fallback to hero image if no optionImageUrl available
        if (!recommendedImageUrl) {
          recommendedImageUrl = heroImages[0];
        }
      }

      // Convert string type to enum
      const productType =
        product.type === 'variable'
          ? SkinSeoulProductType.Variable
          : product.type === 'variation'
            ? SkinSeoulProductType.Variation
            : SkinSeoulProductType.Simple;

      return {
        product,
        productKey,
        displayName: product.name || product.cpItemName || product.cpNameKr || 'Product',
        productType,
        attributePack: product.attributePack,
        attributeSize: product.attributeSize,
        attributeColor: product.attributeColor,
        recommendedImage: recommendedImageUrl,
        is1Pack: product.attributePack === '1pack',
      };
    });
  }, [restructuredProducts, coupangProduct, imageSelection.thumbnails]);

  const sourceImages = useMemo(() => {
    const heroImages = coupangProduct?.heroImageUrls || [];
    const detailedImages = (coupangProduct?.detailedImageUrls || []).filter((url) => !deletedDetailedImages.has(url));

    // Get verified PDP images from /en/ folder only (not root pdp_ images)
    // Supports both formats: legacy /en/pdp_xxx_index.webp and UUID /en/pdp_uuid.webp
    const pdpImagesFromVerified = verifiedImages.filter((url) => {
      const filename = url.split('/').pop() || '';
      const pathSegments = url.split('/');
      const parentFolder = pathSegments[pathSegments.length - 2];

      // Must be in /en/ folder AND start with pdp_
      return parentFolder === 'en' && filename.startsWith('pdp_');
    });

    // Debug logging
    console.info('🖼️ Image Sources Debug:', {
      heroImagesCount: heroImages.length,
      detailedImagesCount: detailedImages.length,
      pdpImagesFromVerifiedCount: pdpImagesFromVerified.length,
      heroImages,
      detailedImages,
      pdpImagesFromVerified,
    });

    // Collect opt_ images from variations (children) to use for variable (parent) thumbnails
    // opt_ images are stored in variations but displayed as thumbnail options for variables
    const optImages: string[] = [];
    restructuredProducts.forEach((product) => {
      const images = (coupangProduct as any)?.optionImages?.[product.cpItemId || ''];
      if (images && Array.isArray(images)) {
        images.forEach((img: string) => {
          if (img && !optImages.includes(img)) {
            optImages.push(img);
          }
        });
      }
    });

    // Collect optionImageUrl from ssRestructuredProducts (saved data)
    const optionImagesFromRestructured: string[] = [];
    const savedRestructuredProducts = matchedProduct.ssRestructuredProducts || [];
    savedRestructuredProducts.forEach((product) => {
      if (product.optionImageUrl && !optionImagesFromRestructured.includes(product.optionImageUrl)) {
        optionImagesFromRestructured.push(product.optionImageUrl);
      }
    });

    // Gallery should NOT include PDP images (pdp_xxx pattern)
    // Gallery = hero images + non-PDP detailed images
    const nonPdpDetailedImages = detailedImages.filter((url) => {
      const filename = url.split('/').pop() || '';
      return !filename.match(/^pdp_\d+_\d+\./);
    });

    const galleryImages = [...heroImages, ...nonPdpDetailedImages].filter((url, idx, arr) => arr.indexOf(url) === idx);

    // Get all thu_ images from verifiedImages
    const thuImagesFromVerified = verifiedImages.filter((url) => {
      const filename = url.split('/').pop() || '';
      return filename.startsWith('thu_');
    });

    // Display hero images, but check mark those stored as thu_ in verifiedImages
    // Deduplicate by URL to avoid showing same image multiple times
    const allHeroImagesForSelection = [...heroImages, ...thuImagesFromVerified];
    const uniqueHeroImagesForSelection = Array.from(new Set(allHeroImagesForSelection));

    return {
      hero: heroImages,
      detailed: detailedImages,
      detailedPdp: pdpImagesFromVerified, // PDP mode: ONLY verified /en/pdp_ images
      gallery: galleryImages,
      opt: optImages,
      optionImages: optionImagesFromRestructured, // First row: option images from restructured products
      heroImagesForSelection: uniqueHeroImagesForSelection, // Gallery editor: display all hero images + thu_ images from VerifiedImages (deduplicated)
    };
  }, [
    coupangProduct,
    restructuredProducts,
    deletedDetailedImages,
    matchedProduct.ssRestructuredProducts,
    verifiedImages,
  ]);

  useEffect(() => {
    // Check dimensions for detailed images (left panel in PDP mode)
    sourceImages.detailed.forEach((url) => {
      if (!imageDimensions[url]) {
        const img = new Image();
        img.onload = () => {
          setImageDimensions((prev) => ({
            ...prev,
            [url]: { width: img.width, height: img.height },
          }));
        };
        img.src = url;
      }
    });
  }, [sourceImages.detailed, imageDimensions]);

  const handleThumbnailSelect = (productKey: string, imageUrl: string) => {
    // If this image is currently in gallery, remove it from gallery
    const updatedGallery = imageSelection.gallery.includes(imageUrl)
      ? imageSelection.gallery.filter((url) => url !== imageUrl)
      : imageSelection.gallery;

    onImageSelectionChange({
      ...imageSelection,
      thumbnails: {
        ...imageSelection.thumbnails,
        [productKey]: imageUrl,
      },
      gallery: updatedGallery,
    });
  };

  const handlePDPToggle = (imageUrl: string) => {
    const isSelected = imageSelection.pdpImages.includes(imageUrl);
    if (isSelected) {
      onImageSelectionChange({
        ...imageSelection,
        pdpImages: imageSelection.pdpImages.filter((url) => url !== imageUrl),
      });
    } else {
      onImageSelectionChange({
        ...imageSelection,
        pdpImages: [...imageSelection.pdpImages, imageUrl],
      });
    }
  };

  const handleGalleryToggle = (imageUrl: string) => {
    const isSelected = imageSelection.gallery.includes(imageUrl);
    if (isSelected) {
      onImageSelectionChange({
        ...imageSelection,
        gallery: imageSelection.gallery.filter((url) => url !== imageUrl),
      });
    } else {
      onImageSelectionChange({
        ...imageSelection,
        gallery: [...imageSelection.gallery, imageUrl],
      });
    }
  };

  const convertImageToWebp = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      // Always convert to webp regardless of input format
      console.info(`🔄 Converting ${file.name} (${file.type}) to WebP format`);

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          ctx.drawImage(img, 0, 0);
          // Convert to webp with 100% quality (lossless)
          const webpDataUrl = canvas.toDataURL('image/webp', 1);
          console.info(`✅ Converted to WebP: ${Math.round(webpDataUrl.length / 1024)}KB`);
          resolve(webpDataUrl);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, productKey?: string) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      // Convert to WebP
      const dataUrl = await convertImageToWebp(file);

      if (productKey) {
        // Featured thumbnail - upload to S3 and update RestructuredProduct
        if (onUploadThumbnailImmediately) {
          const cpItemId = productKey.split('_')[0];
          if (cpItemId) {
            const uploadedUrl = await onUploadThumbnailImmediately(dataUrl, productKey, cpItemId);
            handleThumbnailSelect(productKey, uploadedUrl);
          } else {
            handleThumbnailSelect(productKey, dataUrl);
          }
        } else {
          handleThumbnailSelect(productKey, dataUrl);
        }
      } else {
        // Gallery image - upload to S3 and update VerifiedImages
        const uploadedUrl = await uploadGalleryImageToS3(dataUrl);
        handleGalleryToggle(uploadedUrl);
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image. Please try again.');
    }

    event.target.value = '';
  };

  const uploadGalleryImageToS3 = async (dataUrl: string): Promise<string> => {
    if (!matchedProduct) {
      throw new Error('No matched product available');
    }

    const uuid = crypto.randomUUID().replace(/-/g, '');
    const fileName = `thu_${uuid}.webp`;

    // Get presigned URL
    const presignedResponse = await verifiedImagesClient.getPresignedUrl({
      fileName,
      gpProductId: matchedProduct.gpProductId,
      cpProductId: matchedProduct.cpProductId,
      cpItemId: matchedProduct.cpItemId,
    });

    if (!presignedResponse.success) {
      throw new Error(presignedResponse.message || 'Failed to get presigned URL');
    }

    // Convert data URL to Blob
    const blob = dataURLtoBlob(dataUrl);

    // Upload to S3
    const uploadSuccess = await verifiedImagesClient.uploadToS3(presignedResponse.uploadUrl, blob);

    if (!uploadSuccess) {
      throw new Error('Failed to upload image to S3');
    }

    // Convert to CloudFront URL
    const cloudFrontUrl = convertToCloudFrontUrl(presignedResponse.s3Url);

    // Update VerifiedImages
    await verifiedImagesClient.updateVerifiedImages({
      gpProductId: matchedProduct.gpProductId,
      cpProductId: matchedProduct.cpProductId,
      cpItemId: matchedProduct.cpItemId,
      imageUrlToRemove: [],
      imageUrlToAdd: [cloudFrontUrl],
    });

    // Update original gallery ref to include the new image (avoid unsaved changes warning)
    originalGalleryRef.current = [...originalGalleryRef.current, cloudFrontUrl];

    // Refetch verifiedImages to display the uploaded image immediately
    await refetchVerifiedImages();

    return cloudFrontUrl;
  };

  const dataURLtoBlob = (dataUrl: string): Blob => {
    const arr = dataUrl.split(',');
    const mime = arr[0]?.match(/:(.*?);/)?.[1] || 'image/webp';
    const bstr = atob(arr[1] || '');
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleDragStart = (e: React.DragEvent, imageUrl: string, productKey: string) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('imageUrl', imageUrl);
    e.dataTransfer.setData('productKey', productKey);
  };

  const handleDrop = (e: React.DragEvent, productKey: string) => {
    e.preventDefault();
    const imageUrl = e.dataTransfer.getData('imageUrl');
    if (imageUrl) {
      handleThumbnailSelect(productKey, imageUrl);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleTranslateImage = async (url: string) => {
    // Mark as translating
    setTranslatingImages((prev) => new Set(prev).add(url));

    try {
      // Call translation API
      const result = await sourcingClient.translateImageFromUrl(url, 'en');

      if (result.success && result.translatedUrl) {
        console.info('✅ Translation successful, uploading to S3 with UUID filename...');

        // Download translated image as blob
        const response = await fetch(result.translatedUrl);
        const blob = await response.blob();

        // Upload to S3 with UUID filename: en/pdp_{uuid}.webp
        const uuid = crypto.randomUUID().replace(/-/g, '');
        const fileName = `en/pdp_${uuid}.webp`;

        const presignedResponse = await verifiedImagesClient.getPresignedUrl({
          gpProductId: matchedProduct.gpProductId,
          cpProductId: matchedProduct.cpProductId,
          cpItemId: matchedProduct.cpItemId,
          fileName,
        });

        if (!presignedResponse.success) {
          throw new Error(`Failed to get presigned URL: ${presignedResponse.message}`);
        }

        const uploadSuccess = await verifiedImagesClient.uploadToS3(presignedResponse.uploadUrl, blob);

        if (!uploadSuccess) {
          throw new Error('Failed to upload translated image to S3');
        }

        // Convert to CloudFront URL
        const cloudFrontUrl = convertToCloudFrontUrl(presignedResponse.s3Url);
        console.info('✅ Translated image uploaded with UUID:', cloudFrontUrl);

        // Save to VerifiedImages
        const saveResult = await verifiedImagesClient.updateVerifiedImages({
          gpProductId: matchedProduct.gpProductId,
          cpProductId: matchedProduct.cpProductId,
          cpItemId: matchedProduct.cpItemId,
          imageUrlToAdd: [cloudFrontUrl],
        });

        if (!saveResult.success) {
          console.warn('⚠️ Failed to save to VerifiedImages, but file uploaded successfully');
        }

        // Replace in selection with new UUID URL
        const updatedPdpImages = imageSelection.pdpImages.map((img) => (img === url ? cloudFrontUrl : img));
        onImageSelectionChange({
          ...imageSelection,
          pdpImages: updatedPdpImages,
        });
      } else {
        console.error('Translation failed:', result.message);
        alert(`Translation failed: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Translation failed:', error);
      alert(`Translation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setTranslatingImages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(url);
        return newSet;
      });
    }
  };

  const handlePreviewImage = (url: string) => {
    window.open(url, '_blank');
  };

  const handlePDPDragStart = (index: number) => {
    setDraggedPDPIndex(index);
  };

  const handlePDPDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedPDPIndex === null || draggedPDPIndex === index) {
      return;
    }

    const newPDPImages = [...imageSelection.pdpImages];
    const draggedItem = newPDPImages[draggedPDPIndex];
    if (!draggedItem) {
      return;
    }

    newPDPImages.splice(draggedPDPIndex, 1);
    newPDPImages.splice(index, 0, draggedItem);

    onImageSelectionChange({
      ...imageSelection,
      pdpImages: newPDPImages,
    });

    setDraggedPDPIndex(index);
  };

  const handlePDPDragEnd = () => {
    setDraggedPDPIndex(null);
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) {
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item) {
        continue;
      }

      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (!blob) {
          continue;
        }

        // Convert to data URL
        const reader = new FileReader();
        reader.onload = (event) => {
          const imageUrl = event.target?.result as string;
          if (imageUrl) {
            // Add pasted image to selection
            onImageSelectionChange({
              ...imageSelection,
              pdpImages: [...imageSelection.pdpImages, imageUrl],
            });

            // TODO: Upload to S3 and replace data URL with permanent URL
            console.info('Pasted image from clipboard');
          }
        };
        reader.readAsDataURL(blob);
      }
    }
  };

  const handleSaveGalleryImagesToVerifiedImages = async () => {
    if (!matchedProduct.gpProductId || !matchedProduct.cpDetailedProduct.productId) {
      alert('Product information is missing');
      return;
    }

    setSaveLoading(true);
    try {
      // Find images that were unselected (in original but not in current)
      const imagesToDelete = originalGalleryRef.current.filter(
        (originalUrl) => !imageSelection.gallery.includes(originalUrl),
      );

      // Delete unselected images first
      if (imagesToDelete.length > 0) {
        console.info('🗑️ Deleting unselected gallery images:', imagesToDelete);
        const deleteResult = await verifiedImagesClient.updateVerifiedImages({
          gpProductId: matchedProduct.gpProductId,
          cpProductId: matchedProduct.cpDetailedProduct.productId,
          cpItemId: matchedProduct.cpDetailedProduct.itemId || '',
          imageUrlToRemove: imagesToDelete,
        });

        if (!deleteResult.success) {
          throw new Error(deleteResult.message || 'Failed to delete unselected images');
        }
        console.info('✅ Unselected images deleted');
      }

      // Add checked hero images to verifiedImages (no upload needed, already in S3)
      if (imageSelection.gallery.length > 0) {
        const result = await verifiedImagesClient.updateVerifiedImages({
          gpProductId: matchedProduct.gpProductId,
          cpProductId: matchedProduct.cpDetailedProduct.productId,
          cpItemId: matchedProduct.cpDetailedProduct.itemId || '',
          imageUrlToAdd: imageSelection.gallery,
        });

        if (result.success) {
          console.info('✅ Gallery images saved to VerifiedImages:', imageSelection.gallery);
        } else {
          throw new Error(result.message || 'Failed to save gallery images');
        }
      }

      // Update the original ref to the new state
      originalGalleryRef.current = [...imageSelection.gallery];
      console.info('✅ Gallery images saved successfully');
    } catch (error) {
      console.error('Failed to save gallery images:', error);
      alert(`Failed to save gallery images: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSaveFeaturedThumbnailToVerifiedImages = async () => {
    if (Object.keys(imageSelection.thumbnails).length === 0) {
      alert('No featured thumbnails selected to save');
      return;
    }

    if (!matchedProduct.gpProductId || !matchedProduct.cpDetailedProduct.productId) {
      alert('Product information is missing');
      return;
    }

    setSaveLoading(true);
    try {
      // Build map of cpItemId -> featuredThumbnailUrl
      const thumbnailUpdates: Map<string, string> = new Map();

      // Process each selected thumbnail
      for (const [productKey, imageUrl] of Object.entries(imageSelection.thumbnails)) {
        // Extract cpItemId from productKey format: cpItemId_type_pack_size_color
        const cpItemId = productKey.split('_')[0];

        if (!cpItemId) {
          console.warn(`⚠️ Could not extract cpItemId from productKey: ${productKey}`);
          continue;
        }

        let finalImageUrl = imageUrl;

        // If it's a data URL, upload to S3 first
        if (imageUrl.startsWith('data:')) {
          const timestamp = Date.now();
          const fileName = `thumbnail_${cpItemId}_${timestamp}.webp`;

          const presignedResponse = await verifiedImagesClient.getPresignedUrl({
            gpProductId: matchedProduct.gpProductId,
            cpProductId: matchedProduct.cpDetailedProduct.productId,
            cpItemId: matchedProduct.cpDetailedProduct.itemId || '',
            fileName,
          });

          if (!presignedResponse.success) {
            throw new Error(`Failed to get presigned URL for ${cpItemId}`);
          }

          const blob = await fetch(imageUrl).then((res) => res.blob());
          const uploadSuccess = await verifiedImagesClient.uploadToS3(presignedResponse.uploadUrl, blob);

          if (!uploadSuccess) {
            throw new Error(`Failed to upload image to S3 for ${cpItemId}`);
          }

          finalImageUrl = presignedResponse.s3Url;
        }

        thumbnailUpdates.set(cpItemId, finalImageUrl);
      }

      // Find matching restructured products and prepare update payload
      const productsToUpdate = Array.from(thumbnailUpdates.entries()).map(([cpItemId, featuredThumbnailUrl]) => {
        const matchingProduct = restructuredProducts.find((p) => p.cpItemId === cpItemId);

        return {
          cpItemId,
          featuredThumbnailUrl,
          sku: matchingProduct?.sku || '',
        };
      });

      // Call the update API
      const result = await sourcingClient.updateRestructuredProducts({
        gpProductId: matchedProduct.gpProductId,
        cpProductId: matchedProduct.cpDetailedProduct.productId,
        cpItemId: matchedProduct.cpDetailedProduct.itemId || '',
        restructuredProducts: productsToUpdate,
      });

      if (result.success) {
        console.info('✅ Featured thumbnails updated:', productsToUpdate);
      } else {
        throw new Error(result.message || 'Failed to update featured thumbnails');
      }
    } catch (error) {
      console.error('Failed to save featured thumbnails:', error);
      alert(`Failed to save featured thumbnails: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <>
      {/* Saving Overlay */}
      {saveLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '32px 48px',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
          >
            <Spinner animation="border" variant="primary" style={{ width: '48px', height: '48px' }} />
            <div style={{ marginTop: '16px', fontSize: '18px', fontWeight: 600, color: '#333' }}>Saving...</div>
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
              Please wait while we save your thumbnails
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          flex: 1,
          background: '#f8f9fa',
          overflowY: 'auto',
          padding: '32px',
        }}
      >
        <div style={{ minWidth: '1200px', maxWidth: '1600px', margin: '0 auto' }}>
          {/* Product Images Mode - Combined Thumbnail + Gallery */}
          {editMode === 'thu' && (
            <THUImageEditor
              matchedProduct={matchedProduct}
              productsThatNeedThumbnails={productsThatNeedThumbnails}
              imageSelection={imageSelection}
              sourceImages={sourceImages}
              fileInputRef={fileInputRef}
              saveLoading={saveLoading}
              getSKUFromCpItemId={getSKUFromCpItemId || (() => undefined)}
              onThumbnailSelect={handleThumbnailSelect}
              onDragStart={handleDragStart}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onFileUpload={handleFileUpload}
              onSaveFeaturedThumbnails={handleSaveFeaturedThumbnailToVerifiedImages}
              onGalleryToggle={handleGalleryToggle}
              onSaveGalleryImages={handleSaveGalleryImagesToVerifiedImages}
            />
          )}

          {editMode === 'pdp' && (
            <PDPImageEditor
              ref={pdpEditorRef}
              detailedImages={sourceImages.detailed}
              selectedPdpImages={imageSelection.pdpImages}
              imageDimensions={imageDimensions}
              translatingImages={translatingImages}
              draggedPDPIndex={draggedPDPIndex}
              preloadedImages={preloadedImages}
              selectedTool={selectedTool}
              textSettings={textSettings}
              matchedProduct={matchedProduct}
              imageSelection={imageSelection}
              onPDPToggle={handlePDPToggle}
              onTranslateImage={handleTranslateImage}
              onDeleteDetailedImage={onDeleteDetailedImage}
              onDeletePdpImage={onDeletePdpImage}
              onSaveEditedImage={onSaveEditedImage}
              onPDPDragStart={handlePDPDragStart}
              onPDPDragOver={handlePDPDragOver}
              onPDPDragEnd={handlePDPDragEnd}
              onPaste={handlePaste}
              onPreviewImage={handlePreviewImage}
              onImageSelectionChange={onImageSelectionChange}
            />
          )}
        </div>
      </div>
    </>
  );
};
